import { useState } from "react";
import { GRAD } from "../../../Constants";
import styles from "./SpotDashboard.module.css";

export function DonutChart({ entries, centerValue, centerLabel }) {
  const [hovered, setHovered] = useState(null);
  const colors = GRAD.sky;
  const total = entries.reduce((sum, item) => sum + Number(item.value || 0), 0);

  if (!total) {
    return <div className={styles["sd-empty-chart"]}>No data Available</div>;
  }

  const CX = 130;
  const CY = 130;
  const R = 100;
  const IR = 56;
  const VW = 280;
  const VH = 270;

  let cumulative = 0;

  const slices = entries.map((entry, index) => {
    const value = Number(entry.value || 0);
    const pct = value / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    const mid = (startAngle + endAngle) / 2;

    // ✅ Special case: single slice at 100% — use two circles instead of a path
    const isFull = pct >= 1;

    let d;
    if (isFull) {
      // Draw two semicircles to form a complete donut ring
      d = [
        `M${CX} ${CY - R}`,
        `A${R} ${R} 0 1 1 ${CX} ${CY + R}`,
        `A${R} ${R} 0 1 1 ${CX} ${CY - R}`,
        `M${CX} ${CY - IR}`,
        `A${IR} ${IR} 0 1 0 ${CX} ${CY + IR}`,
        `A${IR} ${IR} 0 1 0 ${CX} ${CY - IR}`,
        "Z",
      ].join(" ");
    } else {
      const x1 = CX + R * Math.cos(startAngle);
      const y1 = CY + R * Math.sin(startAngle);
      const x2 = CX + R * Math.cos(endAngle);
      const y2 = CY + R * Math.sin(endAngle);
      const xi1 = CX + IR * Math.cos(startAngle);
      const yi1 = CY + IR * Math.sin(startAngle);
      const xi2 = CX + IR * Math.cos(endAngle);
      const yi2 = CY + IR * Math.sin(endAngle);
      const largeArc = pct > 0.5 ? 1 : 0;

      d = `M${x1} ${y1}A${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}L${xi2} ${yi2}A${IR} ${IR} 0 ${largeArc} 0 ${xi1} ${yi1}Z`;
    }

    return {
      d,
      pct,
      entry,
      mid,
      isFull,
      color: colors[index % colors.length],
      dx: Math.cos(mid) * 9,
      dy: Math.sin(mid) * 9,
    };
  });

  const hoveredSlice = hovered !== null ? slices[hovered] : null;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className={styles["sd-donut-svg"]}>
      {slices.map((slice, index) => (
        <path
          key={`${slice.entry.label}-${index}`}
          d={slice.d}
          fill={slice.color}
          fillRule={slice.isFull ? "evenodd" : "nonzero"}
          stroke="#FEFEFE"
          strokeWidth={slice.isFull ? 0 : 2.5}
          opacity={hovered === null || hovered === index ? 1 : 0.38}
          transform={!slice.isFull && hovered === index ? `translate(${slice.dx},${slice.dy})` : "translate(0,0)"}
          onMouseEnter={() => setHovered(index)}
          onMouseLeave={() => setHovered(null)}
        />
      ))}


      <text x={CX} y={CY - 7} textAnchor="middle" fontSize="17" fontWeight="600" fill="#393185">
        {`${centerValue}` || `${total.toFixed(0)}%`}
      </text>
      <text x={CX} y={CY + 10} textAnchor="middle" fontSize="9" fill="#727271">
        {`${centerLabel}` || "total share"}
      </text>

      {/* Hover tooltip */}
      {hoveredSlice && (
        <g className={styles["sd-svg-tooltip"]}>
          <rect x="68" y="98" width="124" height="54" rx="7" fill="#FEFEFE" stroke={hoveredSlice.color} />
          <text x="130" y="114" textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#2B2A29">
            {hoveredSlice.entry.label}
          </text>
          <text x="130" y="128" textAnchor="middle" fontSize="10" fontWeight="700" fill={hoveredSlice.color}>
            {Number(hoveredSlice.entry.percentage || 0).toFixed(2)}%
          </text>
          <text x="130" y="141" textAnchor="middle" fontSize="8.5" fill="#727271">
            ₹{Number(hoveredSlice.entry.spendInLakhs || 0).toFixed(2)}L spend
          </text>
        </g>
      )}
    </svg>
  );
}